/**
 * React Query hooks for PF-5a (fieldiq-api#1229): invoices from
 * customer-approved field tickets.
 *
 * Same rules as usePfTickets.ts: every key starts ['pipelineFacility'] and
 * every write invalidates that one prefix (an invoice moves the register, the
 * billable list, the job's invoices and the ticket's invoice link); every read
 * spreads LOAD_FAILURE_QUERY_OPTIONS so a refusal reads as a refusal, never as
 * an empty list.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidatePf, unwrapPf, unwrapPfList } from './usePipelineFacility';
import type { PfBillableGroup, PfInvoice, PfInvoiceGenerate, PfInvoiceRecipient, PfInvoiceRow, PfInvoiceVoided } from '../types/pf-invoices';

type Params = Record<string, string | undefined>;
const clean = (p?: Params) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  Object.entries(p).forEach(([k, v]) => {
    if (v) out[k] = v;
  });
  return Object.keys(out).length ? out : undefined;
};

export const pfInvoiceKeys = {
  invoices: (p?: Params) => ['pipelineFacility', 'invoices', clean(p) ?? {}] as const,
  billable: () => ['pipelineFacility', 'invoicesBillable'] as const,
  invoice: (id: string) => ['pipelineFacility', 'invoice', id] as const,
  recipient: (id: string) => ['pipelineFacility', 'invoiceRecipient', id] as const,
};

function usePfWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidatePf(qc) });
}

export interface PfInvoiceFilters extends Params {
  status?: string;
  customer_id?: string;
  job_id?: string;
}

export function usePfInvoices(filters?: PfInvoiceFilters, enabled = true) {
  return useQuery({
    queryKey: pfInvoiceKeys.invoices(filters),
    queryFn: async () => unwrapPfList<PfInvoiceRow>((await api.pipelineFacility.invoices.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfBillable(enabled = true) {
  return useQuery({
    queryKey: pfInvoiceKeys.billable(),
    queryFn: async () => unwrapPfList<PfBillableGroup>((await api.pipelineFacility.invoices.billable()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfInvoice(id: string | undefined) {
  return useQuery({
    queryKey: pfInvoiceKeys.invoice(id ?? ''),
    queryFn: async () => unwrapPf<PfInvoice>((await api.pipelineFacility.invoices.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Several invoices' details at once — the ticket page asks which live invoice holds a ticket. */
export function usePfInvoiceDetails(ids: string[], enabled = true) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: pfInvoiceKeys.invoice(id),
      queryFn: async () => unwrapPf<PfInvoice>((await api.pipelineFacility.invoices.get(id)).data),
      enabled,
      ...LOAD_FAILURE_QUERY_OPTIONS,
    })),
  });
}

/** Who a send with no `to` would address — the office's write role only (the API gates it as a write). */
export function usePfInvoiceRecipient(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: pfInvoiceKeys.recipient(id ?? ''),
    queryFn: async () => unwrapPf<PfInvoiceRecipient>((await api.pipelineFacility.invoices.recipient(id as string)).data),
    enabled: !!id && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useGeneratePfInvoice = () =>
  usePfWrite(async (body: PfInvoiceGenerate) => unwrapPf<PfInvoice>((await api.pipelineFacility.invoices.generate(body)).data).data);

export const useSendPfInvoice = () =>
  usePfWrite(async ({ id, to, cc }: { id: string; to?: string; cc?: string[] }) => {
    const body: { to?: string; cc?: string[] } = {};
    if (to) body.to = to;
    if (cc && cc.length) body.cc = cc;
    return unwrapPf<PfInvoice & { sentToName?: string | null }>((await api.pipelineFacility.invoices.send(id, body)).data).data;
  });

/**
 * Mark paid goes through Finance's door (PATCH /unified-invoices/:id, api#1232),
 * so a success moves BOTH lists: every Pipeline read, and Finance → Invoices'
 * other-verticals list and its band figures. The PATCH answers with the raw
 * row, which nothing here reads; the refetch draws the result.
 */
export function useMarkPfInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.pipelineFacility.invoices.markPaid(id)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance-invoices-other-verticals'] });
      qc.invalidateQueries({ queryKey: ['invoice-summary-dashboard'] });
      return invalidatePf(qc);
    },
  });
}

/** Resolves to the voided invoice and the ids of the tickets it released (never dropped on the way). */
export const useVoidPfInvoice = () =>
  usePfWrite(async ({ id, reason }: { id: string; reason: string }) => {
    const inv = unwrapPf<PfInvoiceVoided>((await api.pipelineFacility.invoices.void(id, reason)).data).data;
    return { invoice: inv, releasedTicketIds: Array.isArray(inv?.releasedTicketIds) ? inv!.releasedTicketIds : [] };
  });
